import BaseEntity from "../../../core/entity/BaseEntity";
import Entity from "../../../core/entity/Entity";
import type { MusicalNote } from "../MusicalNote";
import { KeyManager } from "./KeyManager";
import { PhraseManager, PhraseStructure } from "./PhraseManager";

/**
 * Information about timing within the current phrase.
 * Provides all necessary context for pattern selection and execution.
 */
export interface TimingInfo {
  /** The overall beat we're on */
  beat: number;
  /** Number of beats since the beginning of the phrase */
  beatInPhrase: number;
  /** Total number of beats in the phrase */
  phraseLength: number;
  /** Beats of fill at the end of the phrase */
  fillLength: number;
  /** What type of fill this phrase has */
  fillType: "small" | "large" | "medium";
  /** Some unique number that acts as a source of entropy for choosing patterns */
  phraseSeed: number;
}

/**
 * Information about the current key and harmonic context.
 */
export interface KeyInfo {
  /** Root note as MIDI number (e.g., 60 = C4) */
  rootNote: number;
  /** Scale intervals from root (e.g., [0,2,4,5,7,9,11] for major) */
  scale: number[];
  /** Current chord function - simple I-V movement */
  currentChord: "I" | "V";
}

/**
 * Complete musical context provided to composers for generation.
 * Contains all information needed to make musical decisions.
 */
export interface MusicalContext {
  /** Current beat number (fractional precision) */
  beatNumber: number;
  /** Timing information for the current phrase */
  timingInfo: TimingInfo;
  /** Information about the next phrase structure (for anticipation) */
  nextPhraseStructure: PhraseStructure | null;
  /** Key and harmonic information */
  keyInfo: KeyInfo;
  /** Intensity level (0-1) from game state */
  intensity: number;
  /** Current tempo in BPM */
  tempo: number;
  /** Deterministic pseudo-random function for variation */
  getRandom: (seed: string) => number;
}

/**
 * Interface for structured musical composers that generate notes
 * based on complete musical context.
 */
export interface MusicalComposer {
  /**
   * Generate notes for the next beat based on current musical context.
   * Should be deterministic - same context always produces same notes.
   */
  generateNotesForBeat(context: MusicalContext): MusicalNote[];
}

/**
 * Central coordinator for the phrase-based music generation system.
 *
 * Manages musical phrases (with integrated fills), key/harmony, and provides complete
 * musical context to instrument composers. Ensures all instruments
 * are coordinated and follow the same musical structure.
 *
 * Architecture:
 * - PhraseManager: Tracks phrase structure and timing (phrases with fills at end)
 * - KeyManager: Manages root note, scale, and I-V harmony
 * - Deterministic generation for immediate parameter response
 */
export class MasterComposer extends BaseEntity implements Entity {
  tags = ["masterComposer"];
  id = "masterComposer";
  pausable = false;

  private phraseManager!: PhraseManager;
  private keyManager!: KeyManager;

  /** Current music parameters */
  private intensity: number = 0.3;
  private tempo: number = 130;

  onAdd() {
    // Initialize core musical management components
    this.phraseManager = new PhraseManager();
    this.keyManager = new KeyManager();
  }

  /**
   * Get complete musical context for the specified beat.
   * This is the main interface used by instrument composers.
   */
  getMusicalContext(beatNumber: number): MusicalContext {
    const timingInfo = this.phraseManager.getTimingInfo(beatNumber);
    const nextPhraseStructure =
      this.phraseManager.getNextPhraseStructure(beatNumber);
    const keyInfo = this.keyManager.getKeyInfo(timingInfo);

    return {
      beatNumber,
      timingInfo,
      nextPhraseStructure,
      keyInfo,
      intensity: this.intensity,
      tempo: this.tempo,
      getRandom: this.createDeterministicRandom(timingInfo.phraseSeed),
    };
  }

  /**
   * Update musical parameters from game state.
   * Changes are applied immediately for responsive generation.
   */
  setParams(params: { intensity?: number; tempo?: number }) {
    if (params.intensity !== undefined) {
      this.intensity = params.intensity;
    }
    if (params.tempo !== undefined) {
      this.tempo = params.tempo;
    }
  }

  getParams() {
    return {
      intensity: this.intensity,
      tempo: this.tempo,
    };
  }

  /**
   * Create deterministic pseudo-random function based on phrase seed.
   * Ensures same phrase always generates same variations.
   */
  private createDeterministicRandom(phraseSeed: number) {
    return (seed: string): number => {
      // Simple hash function for deterministic randomness
      let hash = phraseSeed;
      for (let i = 0; i < seed.length; i++) {
        hash = ((hash << 5) - hash + seed.charCodeAt(i)) & 0xffffffff;
      }
      // Normalize to 0-1 range
      return Math.abs(hash) / 0xffffffff;
    };
  }
}
